import { Alert, AlertIcon, Badge, Button, HStack, Link, Text, VStack, useToast } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { ConfirmDialog, DataTable, PageContainer } from '@/design-system/components';
import { useAuth } from '@/lib/sdk/auth/hooks';
import {
  useLinkCustomMetabaseReport, useMetabaseReports, useRefreshMetabaseReports, useUnlinkCustomMetabaseReport,
} from '@/lib/sdk/metabase-reports/queries';
import type { LinkCustomMetabaseReportRequest, MetabaseReport, MetabaseReportKind } from '@/lib/sdk/metabase-reports/types';
import { LinkCustomReportDialog } from '../components/LinkCustomReportDialog';

const kindLabels: Record<MetabaseReportKind, string> = {
  chatbot: 'Workflow',
  completion_rate: 'Completion rate',
  instant_tasks: 'Instant tasks',
  custom: 'Custom',
};

// Rebuilding a workflow or instant-tasks report replaces its dashboard, so its public
// link changes; completion-rate reports are updated in place.
const linkChangingKinds: MetabaseReportKind[] = ['chatbot', 'instant_tasks'];

const publicUrlWarning = (count: number): string =>
  `${count === 1 ? 'This report gets' : `${count} reports get`} a new public link when rebuilt. ` +
  'Anyone using the old link (bookmarks, shared messages) will need the new one from Reports. ' +
  'Completion-rate cells keep working.';

export function MetabaseReportsPage() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id ?? '';
  const toast = useToast();
  const { data, isLoading } = useMetabaseReports(orgId);
  const refresh = useRefreshMetabaseReports(orgId);
  const linkCustom = useLinkCustomMetabaseReport(orgId);
  const unlinkCustom = useUnlinkCustomMetabaseReport(orgId);
  const [pendingKeys, setPendingKeys] = useState<string[]>([]);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<MetabaseReport | null>(null);

  const reports = useMemo(() => data?.items ?? [], [data]);
  const outdatedKeys = reports.filter((report) => report.outdated && report.refreshable).map((report) => report.key);
  const linkChangingCount = (keys: string[]) =>
    reports.filter((report) => keys.includes(report.key) && linkChangingKinds.includes(report.kind)).length;

  const queueRefresh = (keys: string[]) => {
    refresh.mutate(keys, {
      onSuccess: (response) => {
        setPendingKeys([]);
        toast({ status: 'success', title: `Queued ${response.queued} rebuild${response.queued === 1 ? '' : 's'}`, description: 'Reload in a minute to see the new sync time.' });
      },
      onError: (error) => {
        setPendingKeys([]);
        toast({ status: 'error', title: 'Could not queue the rebuild', description: error instanceof Error ? error.message : undefined });
      },
    });
  };

  const submitLink = (request: LinkCustomMetabaseReportRequest) => {
    linkCustom.mutate(request, {
      onSuccess: (report) => {
        setIsLinkDialogOpen(false);
        toast({ status: 'success', title: `Linked ${report.title}`, description: 'Grant access to it on the Reports page.' });
      },
      onError: (error) => {
        toast({ status: 'error', title: 'Could not link the report', description: error instanceof Error ? error.message : undefined });
      },
    });
  };

  const confirmUnlink = (report: MetabaseReport) => {
    unlinkCustom.mutate(report.key, {
      onSuccess: () => {
        setUnlinkTarget(null);
        toast({ status: 'success', title: `Unlinked ${report.title}` });
      },
      onError: (error) => {
        setUnlinkTarget(null);
        toast({ status: 'error', title: 'Could not unlink the report', description: error instanceof Error ? error.message : undefined });
      },
    });
  };

  // Ask first only when a rebuild would change a public link.
  const requestRefresh = (keys: string[]) => {
    if (linkChangingCount(keys) > 0) {
      setPendingKeys(keys);
      return;
    }
    queueRefresh(keys);
  };

  const columns: ColumnDef<MetabaseReport, unknown>[] = [
    {
      header: 'Report', accessorKey: 'title', size: 280,
      cell: (info) => (
        <VStack align="flex-start" spacing={1}>
          <Text fontWeight="medium">{info.row.original.title}</Text>
          <Badge colorScheme="gray">{kindLabels[info.row.original.kind]}</Badge>
        </VStack>
      ),
    },
    {
      header: 'Version', accessorKey: 'definition_version', size: 140,
      cell: (info) => {
        const report = info.row.original;
        if (report.kind === 'custom') return <Text fontSize="sm">Hand-built</Text>;
        return (
          <HStack spacing={2}>
            <Text fontSize="sm">v{report.definition_version}</Text>
            {report.outdated
              ? <Badge colorScheme="orange">Outdated (v{report.current_version})</Badge>
              : <Badge colorScheme="green">Current</Badge>}
          </HStack>
        );
      },
    },
    {
      header: 'Last synced', accessorKey: 'synced_at', size: 180,
      cell: (info) => <Text fontSize="sm" whiteSpace="nowrap">{new Date(info.getValue() as string).toLocaleString()}</Text>,
    },
    {
      header: 'Links', id: 'links', size: 160,
      cell: (info) => (
        <HStack spacing={3}>
          {info.row.original.dashboard_url && <Link href={info.row.original.dashboard_url} isExternal fontSize="sm">Metabase</Link>}
          {info.row.original.public_url && <Link href={info.row.original.public_url} isExternal fontSize="sm">Public</Link>}
        </HStack>
      ),
    },
    {
      header: '', id: 'actions', size: 120,
      cell: (info) => info.row.original.kind === 'custom' ? (
        <Button
          size="sm" variant="outline" colorScheme="red"
          isDisabled={unlinkCustom.isPending}
          data-testid={`mbr-unlink-${info.row.original.key}`}
          onClick={() => setUnlinkTarget(info.row.original)}
        >
          Unlink
        </Button>
      ) : (
        <Button
          size="sm" variant="outline"
          isDisabled={!info.row.original.refreshable || refresh.isPending}
          title={info.row.original.refreshable ? undefined : 'The workflow is inactive or deleted'}
          data-testid={`mbr-refresh-${info.row.original.key}`}
          onClick={() => requestRefresh([info.row.original.key])}
        >
          Refresh
        </Button>
      ),
    },
  ];

  return (
    <PageContainer title="Metabase Reports">
      <VStack spacing={4} align="stretch">
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Refresh rebuilds a report from the current card definitions in the background. All
          completion-rate reports rebuild together and keep their links.
        </Alert>
        <HStack justify="flex-end">
          <Button
            variant="outline" size="sm"
            data-testid="mbr-link-custom"
            onClick={() => setIsLinkDialogOpen(true)}
          >
            Link custom report
          </Button>
          <Button
            colorScheme="brand" size="sm"
            isDisabled={outdatedKeys.length === 0}
            isLoading={refresh.isPending}
            data-testid="mbr-refresh-outdated"
            onClick={() => requestRefresh(outdatedKeys)}
          >
            Refresh all outdated ({outdatedKeys.length})
          </Button>
        </HStack>
        <DataTable
          data={reports}
          columns={columns}
          isLoading={isLoading}
          getRowId={(report) => report.key}
          emptyMessage="No Metabase reports have been built for this organization."
        />
      </VStack>
      <ConfirmDialog
        isOpen={pendingKeys.length > 0}
        onClose={() => setPendingKeys([])}
        onConfirm={() => queueRefresh(pendingKeys)}
        title="Rebuild with new public links?"
        message={publicUrlWarning(linkChangingCount(pendingKeys))}
        confirmText="Rebuild"
        isLoading={refresh.isPending}
        colorScheme="orange"
      />
      <LinkCustomReportDialog
        isOpen={isLinkDialogOpen}
        onClose={() => setIsLinkDialogOpen(false)}
        onSubmit={submitLink}
        isPending={linkCustom.isPending}
      />
      <ConfirmDialog
        isOpen={unlinkTarget !== null}
        onClose={() => setUnlinkTarget(null)}
        onConfirm={() => unlinkTarget && confirmUnlink(unlinkTarget)}
        title="Unlink custom report?"
        message={`${unlinkTarget?.title ?? 'This report'} is removed from this page and the organization's Reports page, with everyone's access to it, and its public link stops working. The dashboard stays in Metabase.`}
        confirmText="Unlink"
        isLoading={unlinkCustom.isPending}
        colorScheme="red"
      />
    </PageContainer>
  );
}
